import type { Logger } from '../utils/Logger.js';

/**
 * Comprehensive theme configuration for Mermaid diagram styling
 */
export interface MermaidThemeConfig {
  // Core theme identification
  readonly name: string;
  readonly displayName: string;
  readonly description?: string;

  // Node styling configuration
  readonly node: {
    readonly background: string;
    readonly border: string;
    readonly text: string;
    readonly fontFamily: string;
    readonly fontSize: string;
    readonly fontWeight: string;
  };

  // Edge and connection styling
  readonly edge: {
    readonly color: string;
    readonly width: number;
    readonly arrowColor: string;
  };

  // Container presentation
  readonly container: {
    readonly background: string;
    readonly border: string;
    readonly shadow: string;
    readonly borderRadius: string;
    readonly padding: string;
    readonly margin: string;
    readonly minHeight: string;
  };

  // Label and text styling
  readonly label: {
    readonly background: string;
    readonly border: string;
    readonly text: string;
    readonly fontSize: string;
    readonly padding: string;
  };

  // Mermaid CLI theme mapping
  readonly mermaidTheme: 'default' | 'dark' | 'forest' | 'neutral';

  // CSS variables for theme inheritance
  readonly cssVariables?: Record<string, string>;
}

/**
 * Options for CSS generation
 */
export interface CSSGenerationOptions {
  readonly includeContainer?: boolean;
  readonly includeNodeStyles?: boolean;
  readonly includeEdgeStyles?: boolean;
  readonly includeLabelStyles?: boolean;
  readonly useImportant?: boolean;
  readonly prefix?: string;
}

/**
 * CLI configuration options for Mermaid rendering
 */
export interface MermaidCLIOptions {
  readonly parseMMDOptions: {
    readonly backgroundColor: string;
    readonly theme: string;
  };
  readonly outputFormat: string;
  readonly quiet: boolean;
}

/**
 * Theme validation result
 */
interface ThemeValidationResult {
  readonly isValid: boolean;
  readonly errors: string[];
  readonly warnings: string[];
}

/**
 * Theme information without optional properties
 */
export interface ThemeInfo {
  readonly name: string;
  readonly displayName: string;
  readonly description: string;
}

/**
 * Enterprise-grade Mermaid theme registry with comprehensive theme management,
 * validation, and extensible CSS generation capabilities.
 *
 * Features:
 * - Type-safe theme configuration with comprehensive validation
 * - Dynamic CSS generation with customizable options
 * - Theme inheritance and CSS variable support
 * - Extensible architecture for custom themes
 * - Comprehensive error handling and logging
 * - Performance-optimized theme caching
 * - CLI integration for server-side rendering
 */
export class MermaidThemeRegistry {
  private readonly themes = new Map<string, MermaidThemeConfig>();
  private readonly cssCache = new Map<string, string>();
  private readonly logger: Logger | undefined;

  /**
   * Creates a new MermaidThemeRegistry instance
   *
   * @param logger - Optional logger for debugging and monitoring
   */
  constructor(logger?: Logger) {
    this.logger = logger;
    this.initializeBuiltInThemes();
    this.logger?.debug('MermaidThemeRegistry initialized with built-in themes');
  }

  /**
   * Registers a new theme with comprehensive validation
   *
   * @param config - Theme configuration to register
   * @throws {Error} When theme configuration is invalid
   */
  registerTheme(config: MermaidThemeConfig): void {
    const validation = this.validateTheme(config);

    if (!validation.isValid) {
      const errorMessage = `Invalid theme configuration for "${config.name}": ${validation.errors.join(', ')}`;
      this.logger?.error(errorMessage);
      throw new Error(errorMessage);
    }

    if (validation.warnings.length > 0) {
      this.logger?.warn(`Theme "${config.name}" has warnings: ${validation.warnings.join(', ')}`);
    }

    // Create immutable theme configuration
    const immutableConfig = this.freezeThemeConfig(config);
    this.themes.set(config.name, immutableConfig);

    // Clear cache for this theme
    this.invalidateThemeCache(config.name);

    this.logger?.info(`Theme "${config.name}" registered successfully`);
  }

  /**
   * Retrieves theme configuration with fallback to default
   *
   * @param themeName - Name of the theme to retrieve
   * @returns Theme configuration or default theme if not found
   */
  getThemeConfig(themeName: string): MermaidThemeConfig {
    const theme = this.themes.get(themeName);

    if (!theme) {
      this.logger?.warn(`Theme "${themeName}" not found, falling back to default theme`);
      return this.themes.get('modern') ?? this.createDefaultTheme();
    }

    return theme;
  }

  /**
   * Generates CSS for a specific theme with customizable options
   *
   * @param themeName - Name of the theme
   * @param options - CSS generation options
   * @returns Generated CSS string
   */
  generateCSS(themeName: string, options: CSSGenerationOptions = {}): string {
    const cacheKey = this.createCacheKey(themeName, options);

    // Check cache first
    const cachedCSS = this.cssCache.get(cacheKey);
    if (cachedCSS) {
      this.logger?.debug(`Using cached CSS for theme "${themeName}"`);
      return cachedCSS;
    }

    const theme = this.getThemeConfig(themeName);
    const css = this.buildThemeCSS(theme, options);

    // Cache the generated CSS
    this.cssCache.set(cacheKey, css);

    this.logger?.debug(`Generated CSS for theme "${themeName}" (${css.length} characters)`);
    return css;
  }

  /**
   * Generates CLI options for server-side rendering
   *
   * @param themeName - Name of the theme
   * @returns CLI configuration options
   */
  generateCLIOptions(themeName: string): MermaidCLIOptions {
    const theme = this.getThemeConfig(themeName);

    return {
      parseMMDOptions: {
        backgroundColor: this.extractBackgroundColor(theme.container.background),
        theme: theme.mermaidTheme,
      },
      outputFormat: 'svg',
      quiet: true,
    };
  }

  /**
   * Gets list of all available themes
   *
   * @returns Array of theme names
   */
  getAvailableThemes(): string[] {
    return Array.from(this.themes.keys()).sort();
  }

  /**
   * Checks if a specific theme exists
   *
   * @param themeName - Name of the theme to check
   * @returns True if theme exists, false otherwise
   */
  hasTheme(themeName: string): boolean {
    return this.themes.has(themeName);
  }

  /**
   * Gets detailed information about a theme
   *
   * @param themeName - Name of the theme
   * @returns Theme metadata or null if not found
   */
  getThemeInfo(themeName: string): ThemeInfo | null {
    const theme = this.themes.get(themeName);
    if (!theme) {
      return null;
    }

    return {
      name: theme.name,
      displayName: theme.displayName,
      description: theme.description ?? 'No description available',
    };
  }

  /**
   * Clears all cached CSS
   */
  clearCache(): void {
    this.cssCache.clear();
    this.logger?.debug('CSS cache cleared');
  }

  /**
   * Initializes built-in themes with comprehensive configurations
   */
  private initializeBuiltInThemes(): void {
    const themes: MermaidThemeConfig[] = [
      {
        name: 'modern',
        displayName: 'Modern Light',
        description: 'Clean, modern theme with light colors',
        node: {
          background: '#f1f5f9',
          border: '#475569',
          text: '#1e293b',
          fontFamily: 'SF Pro Display, -apple-system, BlinkMacSystemFont, sans-serif',
          fontSize: '14px',
          fontWeight: '500',
        },
        edge: {
          color: '#64748b',
          width: 2,
          arrowColor: '#475569',
        },
        container: {
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          border: '#cbd5e1',
          shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          borderRadius: '12px',
          padding: '2rem',
          margin: '2rem 0',
          minHeight: '150px',
        },
        label: {
          background: 'white',
          border: '#cbd5e1',
          text: '#374151',
          fontSize: '12px',
          padding: '4px 8px',
        },
        mermaidTheme: 'default',
      },
      {
        name: 'awesomeModernDark',
        displayName: 'Awesome Modern Dark',
        description: 'Dark theme with modern styling',
        node: {
          background: '#1e293b',
          border: '#64748b',
          text: '#f1f5f9',
          fontFamily: 'SF Pro Display, -apple-system, BlinkMacSystemFont, sans-serif',
          fontSize: '14px',
          fontWeight: '500',
        },
        edge: {
          color: '#94a3b8',
          width: 2,
          arrowColor: '#cbd5e1',
        },
        container: {
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          border: '#475569',
          shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
          borderRadius: '12px',
          padding: '2rem',
          margin: '2rem 0',
          minHeight: '150px',
        },
        label: {
          background: '#334155',
          border: '#64748b',
          text: '#e2e8f0',
          fontSize: '12px',
          padding: '4px 8px',
        },
        mermaidTheme: 'dark',
      },
      {
        name: 'academic',
        displayName: 'Academic',
        description: 'Professional serif theme for academic documents',
        node: {
          background: '#fefefe',
          border: '#2d3748',
          text: '#1a202c',
          fontFamily: 'Charter, Georgia, Times New Roman, serif',
          fontSize: '13px',
          fontWeight: '400',
        },
        edge: {
          color: '#4a5568',
          width: 1.5,
          arrowColor: '#2d3748',
        },
        container: {
          background: '#ffffff',
          border: '#e2e8f0',
          shadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          borderRadius: '8px',
          padding: '1.5rem',
          margin: '1.5rem 0',
          minHeight: '120px',
        },
        label: {
          background: '#f7fafc',
          border: '#e2e8f0',
          text: '#2d3748',
          fontSize: '11px',
          padding: '3px 6px',
        },
        mermaidTheme: 'default',
      },
      {
        name: 'obsidian-vibrant',
        displayName: 'Obsidian Vibrant',
        description: 'Dark vibrant theme with pink and cyan accents',
        node: {
          background: '#2b2d3e',
          border: '#ff6b9d',
          text: '#e4e6eb',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
          fontSize: '14px',
          fontWeight: '600',
        },
        edge: {
          color: '#4ecdc4',
          width: 2,
          arrowColor: '#ff6b9d',
        },
        container: {
          background: '#1e1e2e',
          border: '#ff6b9d',
          shadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          borderRadius: '8px',
          padding: '2rem',
          margin: '2rem 0',
          minHeight: '150px',
        },
        label: {
          background: '#383a4f',
          border: '#4ecdc4',
          text: '#e4e6eb',
          fontSize: '12px',
          padding: '4px 8px',
        },
        mermaidTheme: 'dark',
        cssVariables: {
          '--bg-primary': '#1e1e2e',
          '--bg-secondary': '#2b2d3e',
          '--bg-tertiary': '#383a4f',
          '--text-primary': '#e4e6eb',
          '--color-pink': '#ff6b9d',
          '--color-cyan': '#4ecdc4',
          '--radius': '8px',
          '--shadow': '0 4px 12px rgba(0, 0, 0, 0.3)',
          '--font-text': 'Inter, system-ui, sans-serif',
        },
      },
      {
        name: 'github',
        displayName: 'GitHub',
        description: 'GitHub-style theme for familiar appearance',
        node: {
          background: '#f6f8fa',
          border: '#d0d7de',
          text: '#24292f',
          fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
          fontSize: '14px',
          fontWeight: '500',
        },
        edge: {
          color: '#656d76',
          width: 1,
          arrowColor: '#24292f',
        },
        container: {
          background: '#ffffff',
          border: '#d0d7de',
          shadow: '0 1px 0 rgba(208, 215, 222, 0.2)',
          borderRadius: '6px',
          padding: '1.5rem',
          margin: '1.5rem 0',
          minHeight: '120px',
        },
        label: {
          background: '#f6f8fa',
          border: '#d0d7de',
          text: '#24292f',
          fontSize: '12px',
          padding: '4px 8px',
        },
        mermaidTheme: 'default',
      },
    ];

    for (const theme of themes) {
      try {
        this.registerTheme(theme);
      } catch (error) {
        this.logger?.error(
          `Failed to register built-in theme "${theme.name}": ${(error as Error).message}`
        );
      }
    }
  }

  /**
   * Validates theme configuration comprehensively
   */
  private validateTheme(config: MermaidThemeConfig): ThemeValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Required fields validation
    if (!config.name || typeof config.name !== 'string') {
      errors.push('Theme name is required and must be a string');
    }

    if (!config.displayName || typeof config.displayName !== 'string') {
      errors.push('Display name is required and must be a string');
    }

    // Node configuration validation
    if (!config.node) {
      errors.push('Node configuration is required');
    } else {
      if (!config.node.background) errors.push('Node background color is required');
      if (!config.node.border) errors.push('Node border color is required');
      if (!config.node.text) errors.push('Node text color is required');
      if (!config.node.fontFamily) errors.push('Node font family is required');
      if (!config.node.fontSize) errors.push('Node font size is required');
      if (!config.node.fontWeight) errors.push('Node font weight is required');
    }

    // Edge configuration validation
    if (!config.edge) {
      errors.push('Edge configuration is required');
    } else {
      if (!config.edge.color) errors.push('Edge color is required');
      if (typeof config.edge.width !== 'number' || config.edge.width <= 0) {
        errors.push('Edge width must be a positive number');
      }
      if (!config.edge.arrowColor) errors.push('Arrow color is required');
    }

    // Container configuration validation
    if (!config.container) {
      errors.push('Container configuration is required');
    } else {
      const requiredContainerFields = [
        'background',
        'border',
        'shadow',
        'borderRadius',
        'padding',
        'margin',
        'minHeight',
      ];
      for (const field of requiredContainerFields) {
        if (
          !(field in config.container) ||
          !config.container[field as keyof typeof config.container]
        ) {
          errors.push(`Container ${field} is required`);
        }
      }
    }

    // Label configuration validation
    if (!config.label) {
      errors.push('Label configuration is required');
    } else {
      const requiredLabelFields = ['background', 'border', 'text', 'fontSize', 'padding'];
      for (const field of requiredLabelFields) {
        if (!(field in config.label) || !config.label[field as keyof typeof config.label]) {
          errors.push(`Label ${field} is required`);
        }
      }
    }

    // Mermaid theme validation
    if (!['default', 'dark', 'forest', 'neutral'].includes(config.mermaidTheme)) {
      errors.push('Mermaid theme must be one of: default, dark, forest, neutral');
    }

    // Warnings for optional but recommended fields
    if (!config.description) {
      warnings.push('Theme description is recommended for better documentation');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * Creates a deep copy of theme configuration (simplified approach)
   */
  private freezeThemeConfig(config: MermaidThemeConfig): MermaidThemeConfig {
    // Simple deep clone without over-engineered freezing that blocks functionality
    return JSON.parse(JSON.stringify(config));
  }

  /**
   * Builds comprehensive CSS for a theme
   */
  private buildThemeCSS(theme: MermaidThemeConfig, options: CSSGenerationOptions): string {
    const {
      includeContainer = true,
      includeNodeStyles = true,
      includeEdgeStyles = true,
      includeLabelStyles = true,
      useImportant = true,
      prefix = '',
    } = options;

    const important = useImportant ? ' !important' : '';
    const cssRules: string[] = [];

    // CSS Variables (if available)
    if (theme.cssVariables && Object.keys(theme.cssVariables).length > 0) {
      const variableRules = Object.entries(theme.cssVariables)
        .map(([key, value]) => `  ${key}: ${value};`)
        .join('\n');

      cssRules.push(`:root {\n${variableRules}\n}`);
    }

    // Container styles
    if (includeContainer) {
      cssRules.push(`${prefix}.mermaid {
  display: flex${important};
  justify-content: center${important};
  align-items: center${important};
  padding: ${theme.container.padding}${important};
  margin: ${theme.container.margin}${important};
  background: ${theme.container.background}${important};
  border: 2px solid ${theme.container.border}${important};
  border-radius: ${theme.container.borderRadius}${important};
  box-shadow: ${theme.container.shadow}${important};
  min-height: ${theme.container.minHeight}${important};
}

${prefix}.mermaid svg {
  max-width: 100%${important};
  height: auto${important};
}`);
    }

    // Node styles
    if (includeNodeStyles) {
      cssRules.push(`${prefix}.mermaid .node rect,
${prefix}.mermaid .node polygon,
${prefix}.mermaid .node circle,
${prefix}.mermaid .node ellipse {
  stroke: ${theme.node.border}${important};
  stroke-width: 2px${important};
  fill: ${theme.node.background}${important};
}

${prefix}.mermaid .node text {
  font-family: ${theme.node.fontFamily}${important};
  font-size: ${theme.node.fontSize}${important};
  font-weight: ${theme.node.fontWeight}${important};
  fill: ${theme.node.text}${important};
}`);
    }

    // Edge styles
    if (includeEdgeStyles) {
      cssRules.push(`${prefix}.mermaid .edgePath path {
  stroke: ${theme.edge.color}${important};
  stroke-width: ${theme.edge.width}px${important};
}

${prefix}.mermaid .arrowhead {
  fill: ${theme.edge.arrowColor}${important};
  stroke: ${theme.edge.arrowColor}${important};
}`);
    }

    // Label styles
    if (includeLabelStyles) {
      cssRules.push(`${prefix}.mermaid .edgeLabel {
  background-color: ${theme.label.background}${important};
  border: 1px solid ${theme.label.border}${important};
  border-radius: 4px${important};
  padding: ${theme.label.padding}${important};
  font-size: ${theme.label.fontSize}${important};
  color: ${theme.label.text}${important};
}`);
    }

    // Print optimizations
    cssRules.push(`@media print {
  ${prefix}.mermaid {
    background: white${important};
    border: 2px solid #94a3b8${important};
    page-break-inside: avoid;
    break-inside: avoid;
    box-shadow: none${important};
  }

  ${prefix}.mermaid svg {
    filter: none${important};
  }
}`);

    return `<style>\n/* Mermaid Theme: ${theme.displayName} */\n${cssRules.join('\n\n')}\n</style>`;
  }

  /**
   * Extracts background color from CSS value (for CLI usage)
   */
  private extractBackgroundColor(backgroundValue: string): string {
    // Handle gradients by extracting the first color
    const gradientMatch = backgroundValue.match(
      /(?:linear-gradient|radial-gradient)\([^)]*?([#\w]+)/
    );
    if (gradientMatch?.[1]) {
      return gradientMatch[1];
    }

    // Handle CSS variables
    const variableMatch = backgroundValue.match(/var\([^,)]+,\s*([^)]+)\)/);
    if (variableMatch?.[1]) {
      return variableMatch[1].trim();
    }

    // Return as-is for solid colors
    return backgroundValue;
  }

  /**
   * Creates cache key for CSS generation
   */
  private createCacheKey(themeName: string, options: CSSGenerationOptions): string {
    const optionsString = JSON.stringify(options);
    return `${themeName}:${optionsString}`;
  }

  /**
   * Invalidates cache entries for a specific theme
   */
  private invalidateThemeCache(themeName: string): void {
    const keysToDelete = Array.from(this.cssCache.keys()).filter((key) =>
      key.startsWith(`${themeName}:`)
    );
    for (const key of keysToDelete) {
      this.cssCache.delete(key);
    }
  }

  /**
   * Creates a basic default theme as fallback
   */
  private createDefaultTheme(): MermaidThemeConfig {
    return {
      name: 'default',
      displayName: 'Default',
      description: 'Basic default theme',
      node: {
        background: '#ffffff',
        border: '#000000',
        text: '#000000',
        fontFamily: 'Arial, sans-serif',
        fontSize: '14px',
        fontWeight: 'normal',
      },
      edge: {
        color: '#000000',
        width: 1,
        arrowColor: '#000000',
      },
      container: {
        background: '#ffffff',
        border: '#cccccc',
        shadow: 'none',
        borderRadius: '4px',
        padding: '1rem',
        margin: '1rem 0',
        minHeight: '100px',
      },
      label: {
        background: '#ffffff',
        border: '#cccccc',
        text: '#000000',
        fontSize: '12px',
        padding: '2px 4px',
      },
      mermaidTheme: 'default',
    };
  }
}
